import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { ArtifactRenderer } from '../artifacts/ArtifactRenderer';
import { CitationList } from './CitationList';
import { ConfidenceBadge } from './ConfidenceBadge';
import { InteractiveOptions } from './InteractiveOptions';
import { normalizeMarkdownTables } from '@/utils/markdown';
import { chat } from '@/lib/api';
import { useChatStore } from '@/stores/chatStore';
import type { Message as MessageType } from '@/types';

interface MessageProps {
  message: MessageType;
}

function FeedbackButtons({
  evaluationId,
  initialFeedback,
}: {
  evaluationId: string;
  initialFeedback?: 'helpful' | 'not_helpful';
}) {
  const [feedback, setFeedback] = useState<'helpful' | 'not_helpful' | null>(initialFeedback ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitFeedback = async (newFeedback: 'helpful' | 'not_helpful') => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await chat.submitUserFeedback(evaluationId, {
        feedback: newFeedback,
      });
      setFeedback(newFeedback);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => submitFeedback('helpful')}
        disabled={isSubmitting}
        className={`rounded p-1 transition-colors ${
          feedback === 'helpful'
            ? 'bg-green-500/20 text-green-600 dark:text-green-400'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        } disabled:opacity-50`}
        title="Helpful"
      >
        <ThumbsUp className="h-4 w-4" />
      </button>
      <button
        onClick={() => submitFeedback('not_helpful')}
        disabled={isSubmitting}
        className={`rounded p-1 transition-colors ${
          feedback === 'not_helpful'
            ? 'bg-red-500/20 text-red-600 dark:text-red-400'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        } disabled:opacity-50`}
        title="Not helpful"
      >
        <ThumbsDown className="h-4 w-4" />
      </button>
    </div>
  );
}

export function Message({ message }: MessageProps) {
  const { sendMessage, isStreaming } = useChatStore();

  const handleOptionSelect = (value: string) => {
    // Send the selected option as a new user message
    sendMessage(value);
  };

  return (
    <div className="py-4">
      <div className="prose prose-sm dark:prose-invert max-w-none text-foreground">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {normalizeMarkdownTables(message.content)}
        </ReactMarkdown>
      </div>

      {message.artifacts && message.artifacts.length > 0 && (
        <div className="mt-4 space-y-4">
          {message.artifacts.map((artifact) => (
            <ArtifactRenderer key={artifact.id} artifact={artifact} />
          ))}
        </div>
      )}

      {/* Interactive options for disambiguation or choices */}
      {message.interactive && (
        <InteractiveOptions
          interactive={message.interactive}
          onSelect={handleOptionSelect}
          disabled={isStreaming}
        />
      )}

      {/* Citation markers and feedback row */}
      {message.role === 'assistant' && (
        (message.citations && message.citations.length > 0) ||
        message.confidenceScore !== undefined ||
        message.evaluationId
      ) && (
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          {message.citations && message.citations.length > 0 && (
            <CitationList citations={message.citations} />
          )}
          {message.confidenceScore !== undefined && (
            <ConfidenceBadge
              score={message.confidenceScore}
              rationale={message.confidenceRationale}
            />
          )}
          {message.evaluationId && (
            <FeedbackButtons evaluationId={message.evaluationId} />
          )}
        </div>
      )}
    </div>
  );
}
